import React, { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useEmployees } from "../../hooks/useEmployees";
import { useRoles } from "../../hooks/useRoles";
import { useStores } from "../../hooks/useStores";
import type { Store } from "@reduxjs/toolkit";
import type { EmployeeItem, RoleItem } from "../../types/employee";
import { createEmployee, deleteEmployees, getEmployees } from "../../slices/employeeSlice";
import { PlusCircle, RefreshCcwIcon, Trash, UserRoundX } from "lucide-react";
import { StoreCardSkeleton } from "../../components/skeletons/StoreCardSkeleton";
import EmployeeCard from "../../components/EmployeeCard";
import AddEditEmployeeModal from "../../components/Forms/AddEditEmployeeModal";
import DeletionConfirmationModal from "../../components/DeletionConfirmationModal";
import { RoleBasedLayout } from "../../components/RoleBasedLayout";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";
import SubscriptionGuardModal from "../../components/SubscriptionGuardModal";
import EntityLimitCard from "../../components/EntityLimitCard";


const EmployeesPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { employees, error, loading } = useEmployees();
    const { usageOverview, permissions, isMaxEmployeeCountReached } = useSubscriptionGuard();
    const { roles } = useRoles();
    const { stores } = useStores();

    // Retrieve only stores that the manager manages
    const storeAssignments = useAppSelector((state) => state.auth.user?.StoreAssignment);
    const assignedStoreIds = new Set(
        storeAssignments?.map((a: any) => a.Store.id)
    );
    const matchedStores: unknown = stores.filter((store: any) =>
        assignedStoreIds.has(store.id)
    );

    const fetchedStores = (matchedStores as Store[]) ?? [];
    const fetchedRoles = (roles as RoleItem[]) ?? [];

    // MODAL VISIBILITY STATES
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showSubscriptionGuardModal, setShowSubscriptionGuardModal] = useState<boolean>(false);
    const [addOpen, setAddOpen] = useState(false);


    // SELECTED EMPLOYEES STATE
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [editEmployee, setEditEmployee] = useState<EmployeeItem | null>(null); // Editing


    // FILTER STATES
    const [filterStore, setFilterStore] = useState<string | "all">("all");
    const [filterLocation, setFilterLocation] = useState<string | "all">("all");



    // SELECTION HANDLERS
    const toggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((p) => p !== id)));
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) setSelectedIds(employees.map((e: EmployeeItem) => e.id));
        else setSelectedIds([]);
    };

    // DELETION HANDLERS
    const deleteSelected = async () => {
        if (!selectedIds.length) return;
        await dispatch(deleteEmployees(selectedIds));
        setSelectedIds([]);
    };

    // CREATION HANDLERS
    const handleAdd = async (emp: EmployeeItem) => {
        console.log("Employee: ", emp);
        await dispatch(createEmployee(emp));
    };


    // UPDATE HANDLERS
    const openEdit = (emp: EmployeeItem) => setEditEmployee(emp);

    const handleEdit = async () => {
        // await dispatch(updateEmployee(emp));
        setEditEmployee(null);
    };


    // derived list from filters
    const filteredEmployees = useMemo(() => {
        return employees.filter((e: EmployeeItem) => {
            if (filterStore !== "all") {
                const hasStore = e.assignedStores?.some((s) => s.id === filterStore);
                if (!hasStore) return false;
            }
            if (filterLocation !== "all") {
                const hasLocation = e.assignedStores?.some((s) => s.location === filterLocation);
                if (!hasLocation) return false;
            }
            return true;
        });
    }, [employees, filterStore, filterLocation]);


    const uniqueLocations = Array.from(new Set(stores.map((s) => s.location).filter(Boolean)));

    return (
        <RoleBasedLayout>
            <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">

                        {/* LARGE SCREEN FILTERS */}
                        <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
                            <span>Filter:</span>

                            <select
                                className="form-input text-sm"
                                value={filterStore}
                                onChange={(e) => setFilterStore(e.target.value as any)}
                            >
                                <option value="all">All Stores</option>
                                {fetchedStores.map((s: any) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="form-input text-sm"
                                value={filterLocation}
                                onChange={(e) => setFilterLocation(e.target.value as any)}
                            >
                                <option value="all">All Locations</option>
                                {uniqueLocations.map((loc: any) => (
                                    <option key={loc} value={loc}>
                                        {loc}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* CURRENT EMPLOYEE COUNT */}
                        <EntityLimitCard
                            limit={permissions?.employeeLimit as number}
                            limitReached={isMaxEmployeeCountReached() as boolean}
                            value={usageOverview?.employeeCount as number}
                            entity="employees"
                        />
                    </div>

                    {/* ACTION BUTTONS (REFRESH, DELETE, ADD) */}
                    <div className="flex items-center lg:justify-end gap-2 text-xs">
                        <button
                            title="Refresh employee data"
                            onClick={() => {
                                dispatch(getEmployees())
                            }}
                            className="px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200"
                        >
                            <RefreshCcwIcon />
                        </button>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                checked={selectedIds.length === filteredEmployees.length && filteredEmployees.length > 0}
                                aria-label="Select all employees"
                            />
                            <button
                                title="Delete selected employees"
                                disabled={!selectedIds.length}
                                onClick={() => setShowDeleteModal(true)}
                                className={`px-3 py-2 rounded-md sm:text-xs md:text-md ${selectedIds.length ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500"}`}
                            >
                                <Trash className="inline mr-2" /> Delete {selectedIds.length > 0 && `(${selectedIds.length})`}
                            </button>
                        </div>

                        <button
                            title="Add new Employee"
                            onClick={() => {
                                if (isMaxEmployeeCountReached()) {
                                    setShowSubscriptionGuardModal(true);
                                } else {
                                    setAddOpen(true)
                                }
                            }
                            }
                            className="px-3 py-2 rounded-md bg-primary text-white flex items-center gap-2 sm:text-xs md:text-md"
                        >
                            <PlusCircle /> Add
                        </button>
                    </div>
                </div>

                {/* mobile filters */}
                <div className="sm:hidden flex gap-2 mb-4 overflow-auto pb-1">
                    <select className="form-input text-sm" value={filterStore} onChange={(e) => setFilterStore(e.target.value as any)}>
                        <option value="all">All Stores</option>
                        {stores.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>

                    <select className="form-input text-sm" value={filterLocation} onChange={(e) => setFilterLocation(e.target.value as any)}>
                        <option value="all">All Locations</option>
                        {uniqueLocations.map((loc: any) => (
                            <option key={loc} value={loc}>
                                {loc}
                            </option>
                        ))}
                    </select>
                </div>


                {/* EMPLOYEE DISPLAY */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {(loading) ? (
                        Array.from({ length: 6 }).map((_, i) => <StoreCardSkeleton key={i} />)
                    ) : employees.length > 0 ? (
                        filteredEmployees.map(
                            (emp: EmployeeItem, index) =>
                                emp && (
                                    <EmployeeCard
                                        key={index}
                                        employee={emp}
                                        selected={selectedIds.includes(emp.id)}
                                        onSelect={toggleSelect}
                                        onEdit={openEdit}
                                    />
                                )
                        )
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-3 text-gray-400 col-span-full py-10">
                            <UserRoundX size={56} />
                            <h1 className="text-gray-600 mt-2 text-center">
                                No employees registered yet
                            </h1>
                            <button
                                title="Add new Employee"
                                onClick={() => {
                                    if (isMaxEmployeeCountReached()) {
                                        setShowSubscriptionGuardModal(true);
                                    } else {
                                        setAddOpen(true)
                                    }
                                }
                                }
                                className="px-3 py-2 rounded-md bg-primary text-white flex items-center gap-2 sm:text-xs md:text-lg"
                            >
                                <PlusCircle /> Add your first Employee
                            </button>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="flex items-center justify-between bg-red-50 text-red-600 border border-red-200 p-3 rounded-md mb-4">
                        <span>Something went wrong. Please try again</span>
                        <button
                            onClick={() => dispatch(getEmployees())}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Add / Edit / Delete modals */}
                <AddEditEmployeeModal
                    open={addOpen}
                    initial={null}
                    stores={stores}
                    roles={fetchedRoles}
                    onClose={() => setAddOpen(false)}
                    onSave={handleAdd}
                />

                <AddEditEmployeeModal
                    open={!!editEmployee}
                    initial={editEmployee}
                    stores={stores}
                    roles={fetchedRoles}
                    onClose={() => setEditEmployee(null)}
                    onSave={handleEdit}
                />

                <DeletionConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false)
                    }}
                    onConfirm={() => {
                        deleteSelected()
                        setShowDeleteModal(false)
                    }}
                    count={selectedIds.length}
                    type="employee"
                />

                {/* SUBSCRIPTION GUARD */}
                <SubscriptionGuardModal
                    isOpen={showSubscriptionGuardModal}
                    onClose={() => setShowSubscriptionGuardModal(false)}
                    type="employees"
                />

            </div>
        </RoleBasedLayout>
    );
};

export default EmployeesPage;
